'use client';

import { usePathname } from 'next/navigation';
import { TopBar } from '@/components/layout/TopBar';
import { FloatingBar } from './FloatingBar';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

interface PublicLayoutShellProps {
  fontVars: string;
  children: React.ReactNode;
}

/**
 * Detects whether the current route is admin or public-facing,
 * and applies the appropriate layout shell + theme scoping.
 */
export function PublicLayoutShell({ fontVars, children }: PublicLayoutShellProps) {
  const pathname = usePathname();
  const t = useTranslations('common');

  // Admin routes: /xx/admin/...
  const isAdmin = /^\/[a-z]{2}\/admin(\/|$)/.test(pathname);

  if (isAdmin) {
    // Admin layout — existing Geist fonts, no public theme
    return (
      <div className={`${fontVars} antialiased flex flex-col min-h-screen`}>
        <TopBar />
        <main className="flex-1">{children}</main>
        <footer className="border-t bg-muted/30">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
              <div className="text-center sm:text-left">{t('copyright')}</div>
              <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-right">
                <Link href="/docs" className="hover:text-foreground transition-colors underline underline-offset-4">
                  {t('footer.documentsAvailable')} - {t('footer.viewDocuments')}
                </Link>
                <span className="hidden sm:inline text-muted-foreground">&bull;</span>
                <Link href="/privacy" className="hover:text-foreground transition-colors underline underline-offset-4">
                  {t('footer.privacyNotice')}
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Public layout — new design system with public theme (mobile-first, constrained on desktop)
  return (
    <div data-public="" className={`${fontVars} antialiased flex flex-col min-h-screen`}>
      <main className="flex-1 relative w-full mx-auto" style={{ maxWidth: 430 }}>
        {children}
      </main>
      <footer
        className="w-full flex justify-center"
        style={{ paddingBottom: 100, paddingTop: 4 }}
      >
        <div
          className="flex items-center gap-3"
          style={{
            fontFamily: 'var(--font-pub-mono)',
            fontSize: 9,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--pub-text-muted)',
            opacity: 0.6,
          }}
        >
          <Link
            href={`/${pathname.split('/')[1]}/privacy`}
            style={{ color: 'inherit' }}
            className="hover:opacity-70 transition-opacity"
          >
            {t('footer.privacyNotice')}
          </Link>
          <span>·</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </footer>
      <FloatingBar />
    </div>
  );
}
